import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Swal from "sweetalert2";
import MenuLateral from "../../Components/MenuLateral";
import { APP_ROUTES } from "../../auth/access";
import { USER_ROLES } from "../../auth/session";
import { LoginContext } from "../../context/LoginContext";
import api from "../../services/api";
import { fetchMediaBlob } from "../../services/media";
import AuthenticatedMedia from "../../Components/AuthenticatedMedia";
import {
  FiAlertCircle,
  FiAlertTriangle,
  FiArrowLeft,
  FiCalendar,
  FiCheckCircle,
  FiChevronLeft,
  FiChevronRight,
  FiClock,
  FiEyeOff,
  FiHelpCircle,
  FiImage,
  FiInbox,
  FiLoader,
  FiMapPin,
  FiPauseCircle,
  FiPlay,
  FiRefreshCw,
  FiSearch,
  FiSend,
  FiSkipForward,
  FiTrash2,
  FiUserCheck,
  FiUserX,
  FiUsers,
  FiX,
  FiXCircle,
  FiZoomIn,
} from "react-icons/fi";
import { executePublicationBatches, initialRecognitionEligibility, summarizeRecognitionSelection } from "./publicationBulk";
import { loadPublicationPhotos } from "./loadPublicationPhotos";
import "./index.css";

const QUEUES = [
  ["all", "Todas"],
  ["input", "Aguardando decisão"],
  ["matched", "Match"],
  ["no_match", "Sem Match"],
  ["general_candidate", "Foto geral"],
  ["failed", "Falha"],
  ["skipped", "Ignorado"],
  ["ready_to_publish", "Disponível para publicação"],
  ["published", "Publicado"],
  ["unavailable", "Indisponível"],
];

const PHOTO_SORT_OPTIONS = [
  ["created_at_desc", "Data de criação — mais recentes"],
  ["created_at_asc", "Data de criação — mais antigas"],
  ["name_asc", "Nome — A–Z"],
  ["name_desc", "Nome — Z–A"],
];

const EVENT_PUBLICATION_STATUS_LABELS = {
  UNPUBLISHED: "Indisponível",
  PUBLISHED: "Publicado",
};

const PHOTO_INPUT_STATUS_LABELS = {
  PENDING: "Aguardando decisão",
  APPROVED: "Aprovado",
  EXCLUDED: "Excluída",
};

const PHOTO_OUTCOME_LABELS = {
  MATCHED: "Match",
  NO_MATCH: "Sem Match",
  GENERAL_CANDIDATE: "Foto geral",
  FAILED: "Falha",
};

const PHOTO_AVAILABILITY_STATUS_LABELS = {
  PROCESSING: "Pendente",
  READY_TO_PUBLISH: "Disponível para publicação",
  PUBLISHED: "Publicado",
  UNAVAILABLE: "Indisponível",
};

const adminCanViewUnavailableThumbnail = (photo, isAdmin) =>
  isAdmin && photo.availabilityStatus === "UNAVAILABLE" && photo.publicationInputStatus !== "EXCLUDED";

const PHOTO_PROCESSING_STATUS_LABELS = {
  PENDING: "Reconhecimento Pendente",
  QUEUED: "Aguardando Reconhecimento",
  PROCESSING: "Processando Reconhecimento",
  PROCESSED: "Reconhecimento Processado",
  FAILED: "Falha no Reconhecimento",
  SKIPPED: "Ignorado",
};

const PHOTO_INPUT_STATUS_ICONS = {
  PENDING: FiClock,
  APPROVED: FiCheckCircle,
  EXCLUDED: FiXCircle,
};

const PHOTO_OUTCOME_ICONS = {
  MATCHED: FiUserCheck,
  NO_MATCH: FiUserX,
  GENERAL_CANDIDATE: FiImage,
  FAILED: FiAlertCircle,
};

const PHOTO_PROCESSING_STATUS_ICONS = {
  PENDING: FiPauseCircle,
  QUEUED: FiInbox,
  PROCESSING: FiLoader,
  PROCESSED: FiCheckCircle,
  FAILED: FiAlertTriangle,
  SKIPPED: FiSkipForward,
};

const PUBLICATION_DISTRIBUTION_SEGMENTS = [
  { key: "pending", label: "Pendente", color: "#f0b43c" },
  { key: "matched", label: "Match", color: "#52b6ff" },
  { key: "noMatch", label: "Sem Match", color: "#f7788a" },
  { key: "generalCandidate", label: "Fotos gerais", color: "#cda8ff" },
  { key: "failed", label: "Falha", color: "#ff7764" },
  { key: "skipped", label: "Ignorado", color: "#8997b5" },
  { key: "readyToPublish", label: "Disponível", color: "#54d6b3" },
  { key: "published", label: "Publicado", color: "#35c98b" },
  { key: "unavailable", label: "Indisponível", color: "#697693" },
  { key: "unclassified", label: "Sem classificação", color: "#bec7d9" },
];

const numberFormatter = new Intl.NumberFormat("pt-BR");

const PUBLICATION_CONFIRMATION_CLASSES = {
  popup: "publication-swal-popup",
  title: "publication-swal-title",
  htmlContainer: "publication-swal-text",
  confirmButton: "publication-swal-confirm",
  cancelButton: "publication-swal-cancel",
};

const formatCount = (value) => numberFormatter.format(Number(value || 0));

const ACTIONS_REQUIRING_UNAVAILABLE_WARNING = new Set(["process", "reprocess", "general", "publish"]);

const confirmUnavailablePhotos = async (count) => {
  if (!count) return true;
  const confirmation = await Swal.fire({
    title: count === 1 ? "Foto indisponível selecionada" : "Fotos indisponíveis selecionadas",
    text: count === 1
      ? "A foto selecionada está indisponível. Deseja confirmar esta ação?"
      : `${formatCount(count)} fotos selecionadas estão indisponíveis. Deseja confirmar esta ação?`,
    icon: "warning",
    showCancelButton: true,
    confirmButtonText: "Continuar",
    cancelButtonText: "Cancelar",
    customClass: PUBLICATION_CONFIRMATION_CLASSES,
  });
  return confirmation.isConfirmed;
};

const getErrorMessage = (error, fallback) => {
  const message = error?.response?.data?.message;
  if (Array.isArray(message)) return message.join(", ");
  if (typeof message === "string" && message.trim()) return message;
  return error?.message || fallback;
};

const label = (value, labels) => labels[value] || value || "Não informado";

const formatDate = (value) => (value ? new Date(value).toLocaleString() : "-");

const statusTone = (value) => {
  const normalized = String(value || "").toUpperCase();
  if (["PUBLISHED"].includes(normalized)) return "published";
  if (["UNAVAILABLE", "EXCLUDED", "FAILED", "NO_MATCH"].includes(normalized)) return "danger";
  if (["PROCESSING", "QUEUED", "PENDING"].includes(normalized)) return "processing";
  if (["READY_TO_PUBLISH", "APPROVED", "MATCHED", "GENERAL_CANDIDATE", "PROCESSED"].includes(normalized)) return "ready";
  return "neutral";
};

function PhotoMetaValue({ value, labels, icons }) {
  const StatusIcon = icons[value] || FiHelpCircle;
  return (
    <span className="publication-photo-meta-value">
      <StatusIcon aria-hidden="true" focusable="false" />
      <span>{label(value, labels)}</span>
    </span>
  );
}

function IconActionButton({ icon, label: actionLabel, className = "", showLabel = false, ...props }) {
  const buttonClassName = [
    "publication-icon-action",
    showLabel ? "publication-icon-action-labeled" : "",
    className,
  ].filter(Boolean).join(" ");

  return (
    <button
      type="button"
      className={buttonClassName}
      aria-label={actionLabel}
      title={actionLabel}
      data-tooltip={actionLabel}
      {...props}
    >
      {icon}
      <span className="publication-icon-action-text">{actionLabel}</span>
      <span className="sr-only">{actionLabel}</span>
    </button>
  );
}

function PublicationPagination({ pagination, disabled, onPageChange, atEnd = false }) {
  const first = (pagination.page - 1) * pagination.limit + 1;
  const last = Math.min(pagination.page * pagination.limit, pagination.total);

  return (
    <nav className={`publication-pagination${atEnd ? " publication-pagination-end" : ""}`}
      aria-label={atEnd ? "Paginação das fotos no fim da lista" : "Paginação das fotos"}>
      <span className="publication-pagination-range">
        Exibindo <strong>{formatCount(first)}–{formatCount(last)}</strong> de {formatCount(pagination.total)} fotos
      </span>
      <div className="publication-pagination-navigation">
        <button type="button" className="publication-button publication-button-ghost"
          disabled={disabled || pagination.page <= 1}
          onClick={() => onPageChange(pagination.page - 1)}>
          <FiChevronLeft aria-hidden="true" /> Página anterior
        </button>
        <span className="publication-pagination-current">Página {pagination.page} de {pagination.totalPages}</span>
        <button type="button" className="publication-button publication-button-ghost"
          disabled={disabled || pagination.page >= pagination.totalPages}
          onClick={() => onPageChange(pagination.page + 1)}>
          Próxima página <FiChevronRight aria-hidden="true" />
        </button>
      </div>
    </nav>
  );
}

function PublicationOperations() {
  const { LoggedUser } = useContext(LoginContext);
  const isAdmin = LoggedUser?.role === USER_ROLES.ADMIN;
  const [searchParams, setSearchParams] = useSearchParams();
  const eventIdFromUrl = searchParams.get("eventId") || "";
  const [events, setEvents] = useState([]);
  const [eventId, setEventId] = useState(eventIdFromUrl);
  const [overview, setOverview] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [queue, setQueue] = useState("all");
  const [sort, setSort] = useState("created_at_desc");
  const [pageSize, setPageSize] = useState(50);
  const [search, setSearch] = useState("");
  const [searchDraft, setSearchDraft] = useState("");
  const [loadState, setLoadState] = useState("idle");
  const [progress, setProgress] = useState({ loaded: 0, total: 0 });
  const requestRef = useRef({ version: 0, controller: null });
  const [selectedIds, setSelectedIds] = useState([]);
  const selectAllVisibleRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [actionId, setActionId] = useState("");
  const [bulkProgress, setBulkProgress] = useState(null);
  const bulkRunningRef = useRef(false);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);
  const recognitionSelection = useMemo(
    () => summarizeRecognitionSelection(photos, selectedIds), [photos, selectedIds],
  );
  const [error, setError] = useState("");
  const [sourceUrl, setSourceUrl] = useState("");
  const sourceCloseButtonRef = useRef(null);
  const sourceTriggerRef = useRef(null);

  const selectedEvent = useMemo(
    () => events.find((event) => event.id === eventId) || null,
    [events, eventId],
  );

  const loadEvents = useCallback(async () => {
    const response = await api.get("/events");
    const data = response.data;
    const nextEvents = Array.isArray(data)
      ? data
      : data?.data || data?.events || data?.items || [];
    setEvents(nextEvents);
    if (!eventId && nextEvents[0]?.id) {
      setEventId(nextEvents[0].id);
      setSearchParams({ eventId: nextEvents[0].id });
    }
  }, [eventId, setSearchParams]);

  const invalidateRequest = useCallback(() => {
    requestRef.current.controller?.abort();
    requestRef.current = { version: requestRef.current.version + 1, controller: null };
  }, []);

  const loadPublication = useCallback(async (requestedPage = 1) => {
    invalidateRequest();
    const controller = new AbortController();
    const version = requestRef.current.version;
    requestRef.current.controller = controller;
    const isCurrent = () => requestRef.current.version === version && !controller.signal.aborted;
    setSelectedIds([]);
    setPhotos([]);
    setPagination(null);
    setProgress({ loaded: 0, total: 0 });
    setError("");
    if (!eventId) {
      setOverview(null);
      setLoadState("idle");
      setLoading(false);
      return;
    }
    setLoading(true);
    setLoadState("loading");
    try {
      const [overviewResponse, result] = await Promise.all([
        api.get(`/publication/events/${eventId}/overview`, { signal: controller.signal }),
        loadPublicationPhotos({
          eventId, queue, sort, search, page: requestedPage, pageSize,
          signal: controller.signal,
          onProgress: (next) => {
            if (!isCurrent()) return;
            setProgress({ loaded: next.loaded, total: next.total });
            setPhotos(next.photos);
            setPagination({ page: 1, total: next.total, totalPages: 1 });
          },
        }),
      ]);
      if (!isCurrent()) return;
      setOverview(overviewResponse.data || null);
      setPhotos(result.photos);
      setPagination(result.pagination);
      setLoadState("complete");
    } catch (requestError) {
      if (!isCurrent()) return;
      controller.abort();
      setLoadState("failed");
      setError(getErrorMessage(requestError, "Não foi possível carregar a operação de publicação."));
    } finally {
      if (requestRef.current.version === version) setLoading(false);
    }
  }, [eventId, queue, sort, search, pageSize, invalidateRequest]);

  const changeQuery = (setter, value) => {
    invalidateRequest();
    setSelectedIds([]);
    setPhotos([]);
    setPagination(null);
    setLoadState("loading");
    setter(value);
  };

  const cancelLoading = () => {
    invalidateRequest();
    setSelectedIds([]);
    setLoading(false);
    setLoadState("cancelled");
  };

  useEffect(() => {
    loadEvents().catch((requestError) => {
      setError(getErrorMessage(requestError, "Não foi possível carregar os eventos."));
      setLoading(false);
    });
  }, [loadEvents]);

  useEffect(() => {
    loadPublication();
    return invalidateRequest;
  }, [loadPublication, invalidateRequest]);

  useEffect(() => {
    return () => {
      if (sourceUrl) URL.revokeObjectURL(sourceUrl);
    };
  }, [sourceUrl]);

  useEffect(() => {
    if (!sourceUrl) return undefined;

    sourceCloseButtonRef.current?.focus();
    const handleKeyDown = (keyboardEvent) => {
      if (keyboardEvent.key === "Escape") {
        keyboardEvent.preventDefault();
        setSourceUrl("");
        window.requestAnimationFrame(() => sourceTriggerRef.current?.focus());
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [sourceUrl]);

  const changeEvent = (nextEventId) => {
    if (nextEventId === eventId) return;
    setOverview(null);
    changeQuery(setEventId, nextEventId);
    setSearchParams(nextEventId ? { eventId: nextEventId } : {});
  };

  const refresh = () => loadPublication(pagination?.page || 1);

  const executeAction = async (photo, action) => {
    if (action === "exclude" && !isAdmin) return;
    const labels = {
      process: "iniciar o recognition",
      reprocess: "reprocessar o recognition",
      exclude: "excluir a foto",
      general: "classificar a foto como geral",
      "remove-general": "retirar a classificação Foto Geral",
      publish: "disponibilizar a foto",
      unavailable: "marcar a foto como indisponível",
      "thumbnail/retry": "tentar gerar a miniatura novamente",
    };
    if (ACTIONS_REQUIRING_UNAVAILABLE_WARNING.has(action)
      && photo.availabilityStatus === "UNAVAILABLE"
      && !await confirmUnavailablePhotos(1)) return;
    const confirmation = await Swal.fire({
      title: "Confirmar operação?",
      text: action === "exclude"
        ? `Deseja excluir a foto "${photo.originalName}" da continuidade?`
        : `Deseja ${labels[action] || "executar esta operação"}?`,
      icon: action === "exclude" || action === "unavailable" ? "warning" : "question",
      showCancelButton: true,
      confirmButtonText: "Confirmar",
      cancelButtonText: "Cancelar",
      customClass: PUBLICATION_CONFIRMATION_CLASSES,
    });
    if (!confirmation.isConfirmed) return;

    setActionId(photo.id);
    try {
      if (action === "remove-general") {
        await api.delete(`/publication/photos/${photo.id}/general`);
      } else {
        await api.post(`/publication/photos/${photo.id}/${action}`);
      }
      await loadPublication(pagination?.page || 1);
      Swal.fire("Operação registrada", "O estado persistido foi atualizado.", "success");
    } catch (requestError) {
      Swal.fire("Operação não concluída", getErrorMessage(requestError, "Atualize a fila e tente novamente."), "error");
    } finally {
      setActionId("");
    }
  };

  const executeBulk = async (action) => {
    if (bulkRunningRef.current || actionId || !selectedIds.length || loadState !== "complete") return;
    const ids = action === "process" ? recognitionSelection.eligibleIds : [...selectedIds];
    if (!ids.length) return;
    const omittedCount = action === "process" ? recognitionSelection.ineligible.length : 0;
    bulkRunningRef.current = true;
    setActionId("bulk-confirmation");
    try {
      const affectedIds = new Set(ids);
      const unavailableCount = ACTIONS_REQUIRING_UNAVAILABLE_WARNING.has(action)
        ? photos.filter((photo) => affectedIds.has(photo.id) && photo.availabilityStatus === "UNAVAILABLE").length
        : 0;
      if (!await confirmUnavailablePhotos(unavailableCount) || !mountedRef.current) return;
      const confirmation = await Swal.fire({
        title: "Aplicar operação em lote?",
        text: `${ids.length} foto(s) serão enviadas no evento atual.${omittedCount ? ` ${omittedCount} não elegível(is) não serão enviadas.` : ""}`,
        icon: "question", showCancelButton: true,
        confirmButtonText: "Aplicar", cancelButtonText: "Cancelar",
        customClass: PUBLICATION_CONFIRMATION_CLASSES,
      });
      if (!confirmation.isConfirmed || !mountedRef.current) return;
      setActionId("bulk");
      await executePublicationBatches({
        eventId, action, photoIds: ids,
        shouldContinue: () => mountedRef.current,
        onProgress: (next) => { if (mountedRef.current) setBulkProgress(next); },
      });
      if (!mountedRef.current) return;
      // Always reconcile with the server; an uncertain batch is never replayed automatically.
      await loadPublication(pagination?.page || 1);
    } catch (requestError) {
      if (mountedRef.current) {
        setSelectedIds([]);
        setLoadState("failed");
        setError(getErrorMessage(requestError, "Operação interrompida. Atualize a fila e confira o estado das fotos antes de repetir."));
      }
    } finally {
      bulkRunningRef.current = false;
      if (mountedRef.current) {
        setActionId("");
        setBulkProgress(null);
      }
    }
  };

  const inspectSource = async (photo) => {
    sourceTriggerRef.current = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;
    setActionId(`source-${photo.id}`);
    try {
      const blob = await fetchMediaBlob(`/media/event-photos/${photo.id}/recognition-source`);
      if (sourceUrl) URL.revokeObjectURL(sourceUrl);
      setSourceUrl(URL.createObjectURL(blob));
    } catch (requestError) {
      Swal.fire("Fonte indisponível", getErrorMessage(requestError, "A fonte não pôde ser carregada."), "error");
    } finally {
      setActionId("");
    }
  };

  const closeSource = () => {
    setSourceUrl("");
    window.requestAnimationFrame(() => sourceTriggerRef.current?.focus());
  };

  const selectionDisabled = loading || loadState !== "complete" || Boolean(actionId);

  const toggleSelected = (photoId) => {
    if (selectionDisabled) return;
    setSelectedIds((current) =>
      current.includes(photoId)
        ? current.filter((id) => id !== photoId)
        : [...current, photoId],
    );
  };

  const visiblePhotoIds = useMemo(() => photos.map((photo) => photo.id), [photos]);
  const selectedVisibleCount = visiblePhotoIds.filter((photoId) => selectedIds.includes(photoId)).length;
  const allVisibleSelected = visiblePhotoIds.length > 0 && selectedVisibleCount === visiblePhotoIds.length;
  const someVisibleSelected = selectedVisibleCount > 0 && !allVisibleSelected;

  useEffect(() => {
    if (selectAllVisibleRef.current) {
      selectAllVisibleRef.current.indeterminate = someVisibleSelected;
    }
  }, [someVisibleSelected]);

  const toggleAllVisible = () => {
    if (selectionDisabled) return;
    setSelectedIds((current) => {
      const visibleIds = new Set(visiblePhotoIds);
      if (allVisibleSelected) return current.filter((photoId) => !visibleIds.has(photoId));
      return [...new Set([...current, ...visiblePhotoIds])];
    });
  };

  const counts = overview?.counts || {};
  return (
    <div className="pagina-publication-operations">
      <MenuLateral />
      <main className="publication-operations-content">
        <header className="publication-operations-header">
          <div className="publication-header-copy">
            <span className="publication-eyebrow">Operação administrativa</span>
            <h1>Publicação de fotos</h1>
            <p>Revise o reconhecimento, resolva pendências e disponibilize cada foto.</p>
          </div>
          <div className="publication-operations-actions">
            <Link className="publication-button publication-button-secondary" to={APP_ROUTES.EVENTS}>
              <FiArrowLeft aria-hidden="true" />
              Eventos
            </Link>
            <button
              type="button"
              className="publication-button publication-button-primary"
              onClick={refresh}
              disabled={loading || Boolean(actionId)}
            >
              <FiRefreshCw aria-hidden="true" />
              {loading ? "Atualizando..." : "Atualizar"}
            </button>
          </div>
        </header>

        <section className="publication-event-selector" aria-labelledby="publication-event-selector-title">
          <div className="publication-event-selector-heading">
            <span className="publication-panel-kicker">Contexto da operação</span>
            <h2 id="publication-event-selector-title">Evento em foco</h2>
            <p>Escolha o evento para revisar e publicar o acervo correto.</p>
          </div>
          <div className="publication-event-selector-control">
            <label htmlFor="publication-event">Evento</label>
            <select
              id="publication-event"
              value={eventId}
              onChange={(event) => changeEvent(event.target.value)}
              disabled={Boolean(actionId)}
            >
              <option value="">Selecione um evento</option>
              {events.map((event) => (
                <option key={event.id} value={event.id}>{event.name}</option>
              ))}
            </select>
          </div>
          <div className="publication-event-summary">
            <span className="publication-event-summary-icon" aria-hidden="true"><FiMapPin /></span>
            <span>
              <strong>{selectedEvent?.name || "Nenhum evento selecionado"}</strong>
              <small>{selectedEvent?.location || "Selecione um evento para começar"}</small>
            </span>
          </div>
        </section>

        {error ? (
          <section className="publication-operations-state error" role="alert">
            <p>{error}</p>
            <button type="button" onClick={refresh}>Tentar novamente</button>
          </section>
        ) : null}

        {overview ? (
          <>
            <section className="publication-lifecycle-card" aria-label="Resumo do evento">
              <div className="publication-lifecycle-primary">
                <span className="publication-panel-kicker">Estado do evento</span>
                <strong className={`publication-lifecycle-status status-tone-${statusTone(overview.publicationStatus)}`}>
                  {label(overview.publicationStatus, EVENT_PUBLICATION_STATUS_LABELS)}
                </strong>
                <small>Publicado em: {formatDate(overview.publishedAt)}</small>
              </div>
              <div className="publication-lifecycle-stat">
                <span>Acervo publicado</span>
                <strong>{formatCount(counts.published)} fotos</strong>
                <small>O evento fica disponível após a primeira foto publicada</small>
              </div>
            </section>

            <PublicationOverviewPanel distribution={overview.distribution} counts={counts} />
          </>
        ) : null}

        {eventId ? (
          <section className="publication-queue-section" aria-labelledby="publication-queue-title">
            <div className="publication-queue-toolbar">
              <div className="publication-queue-heading">
                <div>
                  <span className="publication-panel-kicker">Revisão por foto</span>
                  <h2 id="publication-queue-title">Fila administrativa</h2>
                </div>
                <span className="publication-queue-count" role="status">
                  {pagination
                    ? `${formatCount(pagination.total)} ${pagination.total === 1 ? "foto" : "fotos"} no filtro`
                    : loading ? "Carregando fotos" : "0 fotos no filtro"}
                </span>
              </div>
              <div className="publication-queue-controls">
                <form
                  className="publication-search"
                  aria-label="Busca por nome do arquivo"
                  onSubmit={(event) => {
                    event.preventDefault();
                    if (searchDraft !== search) changeQuery(setSearch, searchDraft);
                  }}
                >
                  <label htmlFor="publication-search">Nome do arquivo</label>
                  <div className="publication-search-actions">
                    <div className="publication-search-field">
                      <FiSearch aria-hidden="true" />
                      <input id="publication-search" type="search" maxLength={255}
                        value={searchDraft} onChange={(event) => setSearchDraft(event.target.value)}
                        disabled={Boolean(actionId)} placeholder="Ex.: formatura-01.jpg" />
                    </div>
                    <button type="submit" className="publication-button publication-button-primary"
                      disabled={Boolean(actionId)}>Buscar</button>
                    <button type="button" className="publication-button publication-button-ghost"
                      disabled={Boolean(actionId) || (!search && !searchDraft)}
                      onClick={() => {
                        setSearchDraft("");
                        if (search) changeQuery(setSearch, "");
                      }}>Limpar busca</button>
                  </div>
                </form>
                <label htmlFor="publication-queue">
                  <span>Fila</span>
                  <select id="publication-queue" value={queue} onChange={(event) => changeQuery(setQueue, event.target.value)} disabled={Boolean(actionId)}>
                    {QUEUES.map(([value, text]) => <option key={value} value={value}>{text}</option>)}
                  </select>
                </label>
                <label htmlFor="publication-sort">
                  <span>Ordenar por</span>
                  <select id="publication-sort" value={sort} onChange={(event) => changeQuery(setSort, event.target.value)} disabled={Boolean(actionId)}>
                    {PHOTO_SORT_OPTIONS.map(([value, text]) => <option key={value} value={value}>{text}</option>)}
                  </select>
                </label>
                <label htmlFor="publication-page-size">
                  <span>Fotos por página</span>
                  <select id="publication-page-size" value={pageSize} disabled={Boolean(actionId)}
                    onChange={(event) => changeQuery(setPageSize,
                      event.target.value === "all" ? "all" : Number(event.target.value))}>
                    <option value={10}>10</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                    <option value="all">Todas</option>
                  </select>
                </label>
                <div className="publication-selection-controls">
                  <label className="publication-select-all">
                    <input
                      ref={selectAllVisibleRef}
                      type="checkbox"
                      checked={allVisibleSelected}
                      onChange={toggleAllVisible}
                      disabled={selectionDisabled || !visiblePhotoIds.length}
                      aria-label="Selecionar todos"
                    />
                    <span>{pageSize === "all" ? "Selecionar todas as fotos" : "Selecionar todas as fotos desta página"}</span>
                  </label>
                  <button className="publication-button publication-button-ghost" type="button" onClick={() => setSelectedIds([])} disabled={!selectedIds.length || Boolean(actionId)}>Limpar seleção</button>
                </div>
              </div>
            </div>

            {selectedIds.length ? (
              <div className="publication-bulk-toolbar" role="status" aria-label="Ações das fotos selecionadas" aria-live="polite">
                <strong>{selectedIds.length} selecionada(s)</strong>
                <span>{recognitionSelection.eligibleIds.length} elegível(is) para reconhecimento; {recognitionSelection.ineligible.length} não elegível(is).</span>
                <button type="button" onClick={() => executeBulk("process")} disabled={selectionDisabled || !recognitionSelection.eligibleIds.length}>Processar reconhecimento facial</button>
                <button type="button" onClick={() => executeBulk("general")} disabled={selectionDisabled}>Classificar geral</button>
                <button type="button" onClick={() => executeBulk("publish")} disabled={selectionDisabled}>Publicar</button>
                <button type="button" className="secondary" onClick={() => executeBulk("unavailable")} disabled={selectionDisabled}>Marcar indisponíveis</button>
              </div>
            ) : null}

            {bulkProgress ? (
              <div className="publication-operations-state" role="status" aria-live="polite">
                Enviadas {bulkProgress.sent} de {bulkProgress.total} fotos.
                {" "}{bulkProgress.confirmed} ação(ões) confirmada(s); {bulkProgress.failed} falha(s);
                {" "}{bulkProgress.unconfirmed} sem confirmação.
              </div>
            ) : null}

            {loading ? (
              <div className="publication-operations-state" role="status">
                {pageSize === "all"
                  ? `Carregando todas: ${formatCount(progress.loaded)} de ${formatCount(progress.total)} fotos.`
                  : "Carregando fila..."}
                {pageSize === "all" ? (
                  <button type="button" className="publication-button publication-button-ghost"
                    onClick={cancelLoading}>Cancelar carregamento</button>
                ) : null}
              </div>
            ) : null}
            {loadState === "cancelled" || (loadState === "failed" && photos.length > 0) ? (
              <div className="publication-operations-state" role="status">
                {loadState === "cancelled" ? "Carregamento cancelado." : "Carregamento interrompido."}
                {` Lista parcial: ${formatCount(photos.length)} fotos. Atualize para habilitar a seleção.`}
              </div>
            ) : null}
            {pageSize !== "all" && pagination?.total > 0 ? (
              <PublicationPagination pagination={pagination} disabled={selectionDisabled}
                onPageChange={loadPublication} />
            ) : null}
            {!loading && loadState === "complete" && !photos.length ? <div className="publication-operations-state">Nenhuma foto no filtro atual.</div> : null}
            <div className="publication-photo-list">
              {photos.map((photo) => (
                <article className={`publication-photo-card status-tone-${statusTone(photo.availabilityStatus)}`} key={photo.id}>
                  <div className="publication-photo-select">
                    <input
                      type="checkbox"
                      disabled={selectionDisabled}
                      checked={selectedIds.includes(photo.id)}
                      onChange={() => toggleSelected(photo.id)}
                      aria-label={`Selecionar ${photo.originalName}`}
                    />
                  </div>
                  <div className={`publication-photo-thumbnail ${photo.thumbnail?.status || "pending"}`}>
                    {photo.thumbnail?.status === "completed" && photo.thumbnail?.locator ? (
                      <button
                        type="button"
                        className={`publication-photo-thumbnail-button${adminCanViewUnavailableThumbnail(photo, isAdmin) ? " publication-photo-thumbnail-button--unavailable" : ""}`}
                        onClick={() => inspectSource(photo)}
                        disabled={selectionDisabled || !photo.sourceAvailable}
                        aria-label={`Abrir fonte de ${photo.originalName}`}
                      >
                        {(photo.availabilityStatus === "UNAVAILABLE" && !adminCanViewUnavailableThumbnail(photo, isAdmin)) || photo.publicationInputStatus === "EXCLUDED" ? (
                          <span className="publication-photo-thumbnail-placeholder">Miniatura indisponível</span>
                        ) : (
                          <AuthenticatedMedia
                            path={photo.thumbnail.locator}
                            alt={`Miniatura de ${photo.originalName}`}
                            loading="lazy"
                            decoding="async"
                            className="publication-photo-thumbnail-image"
                            loadingFallback={<span className="publication-photo-thumbnail-placeholder">Carregando miniatura...</span>}
                            fallback={<span className="publication-photo-thumbnail-placeholder">Não foi possível carregar a miniatura</span>}
                          />
                        )}
                        {adminCanViewUnavailableThumbnail(photo, isAdmin) ? (
                          <span className="publication-photo-thumbnail-unavailable-label" aria-hidden="true">Indisponível</span>
                        ) : null}
                      </button>
                    ) : photo.thumbnail?.status === "failed" ? (
                      <div className="publication-photo-thumbnail-placeholder failed" role="status">
                        <span>Falha na miniatura</span>
                        <IconActionButton
                          icon={<FiRefreshCw aria-hidden="true" />}
                          label="Tentar novamente"
                          className="publication-icon-action-retry"
                          onClick={() => executeAction(photo, "thumbnail/retry")}
                          disabled={selectionDisabled}
                        />
                      </div>
                    ) : (
                      <div className="publication-photo-thumbnail-placeholder" role="status">
                        {photo.thumbnail?.status === "processing" ? "Gerando miniatura..." : "Miniatura na fila"}
                      </div>
                    )}
                  </div>
                  <div className="publication-photo-body">
                    <div className="publication-photo-title">
                      <strong>{photo.originalName}</strong>
                      <span className={`publication-photo-status status-tone-${statusTone(photo.availabilityStatus)}`}>
                        {label(photo.availabilityStatus, PHOTO_AVAILABILITY_STATUS_LABELS)}
                      </span>
                    </div>
                    <dl className="publication-photo-meta" aria-label="Dados da foto">
                      <div className="publication-photo-meta-item">
                        <dt>Entrada</dt>
                        <dd><PhotoMetaValue value={photo.publicationInputStatus}
                          labels={PHOTO_INPUT_STATUS_LABELS} icons={PHOTO_INPUT_STATUS_ICONS} /></dd>
                      </div>
                      <div className="publication-photo-meta-item">
                        <dt>Reconhecimento</dt>
                        <dd><PhotoMetaValue value={photo.recognitionOutcome}
                          labels={PHOTO_OUTCOME_LABELS} icons={PHOTO_OUTCOME_ICONS} /></dd>
                      </div>
                      <div className="publication-photo-meta-item">
                        <dt>Processamento</dt>
                        <dd><PhotoMetaValue value={photo.processingStatus?.toUpperCase()}
                          labels={PHOTO_PROCESSING_STATUS_LABELS} icons={PHOTO_PROCESSING_STATUS_ICONS} /></dd>
                      </div>
                      <div className="publication-photo-meta-item">
                        <dt>Criada em</dt>
                        <dd>
                          <span className="publication-photo-meta-value">
                            <FiCalendar aria-hidden="true" focusable="false" />
                            {photo.createdAt ? (
                              <time dateTime={photo.createdAt}>{formatDate(photo.createdAt)}</time>
                            ) : <span>-</span>}
                          </span>
                        </dd>
                      </div>
                    </dl>
                    <div className="publication-photo-actions" aria-label="Ações disponíveis">
                      <span className="publication-photo-actions-label">Ações</span>
                      <div className="publication-photo-action-list">
                        {photo.sourceAvailable ? (
                          <IconActionButton
                            icon={<FiZoomIn aria-hidden="true" />}
                            label="Visualizar"
                            showLabel
                            onClick={() => inspectSource(photo)}
                            disabled={selectionDisabled}
                          />
                        ) : null}
                        {photo.processingStatus === "pending" ? (
                          <IconActionButton
                            icon={<FiPlay aria-hidden="true" />}
                            label="Processar"
                            showLabel
                            onClick={() => executeAction(photo, "process")}
                            title={initialRecognitionEligibility(photo).reasons.map((reason) => reason.message).join(" ") || "Processar"}
                            disabled={selectionDisabled || !initialRecognitionEligibility(photo).eligible}
                          />
                        ) : null}
                        {photo.processingStatus === "failed" || photo.processingStatus === "skipped" ? (
                          <IconActionButton
                            icon={<FiRefreshCw aria-hidden="true" />}
                            label="Reprocessar"
                            showLabel
                            className="secondary"
                            onClick={() => executeAction(photo, "reprocess")}
                            disabled={selectionDisabled}
                          />
                        ) : null}
                        {photo.audienceScope === "EVENT_GENERAL" ? (
                          <IconActionButton
                            icon={<FiUsers aria-hidden="true" />}
                            label="Foto Geral"
                            showLabel
                            className="publication-icon-action-general-active"
                            aria-pressed="true"
                            onClick={() => executeAction(photo, "remove-general")}
                            disabled={selectionDisabled}
                          />
                        ) : photo.recognitionOutcome !== "MATCHED" && ["processed", "skipped", "failed"].includes(photo.processingStatus) && photo.availabilityStatus !== "PUBLISHED" ? (
                          <IconActionButton
                            icon={<FiUsers aria-hidden="true" />}
                            label="Classificar geral"
                            showLabel
                            className="publication-icon-action-general-inactive"
                            aria-pressed="false"
                            onClick={() => executeAction(photo, "general")}
                            disabled={selectionDisabled}
                          />
                        ) : null}
                        {photo.availabilityStatus === "READY_TO_PUBLISH" || (photo.availabilityStatus === "UNAVAILABLE" && photo.publicationInputStatus === "APPROVED" && photo.sourceAvailable) ? (
                          <IconActionButton
                            icon={<FiSend aria-hidden="true" />}
                            label="Publicar"
                            showLabel
                            className="publication-icon-action-success"
                            onClick={() => executeAction(photo, "publish")}
                            disabled={selectionDisabled}
                          />
                        ) : null}
                        {photo.availabilityStatus !== "UNAVAILABLE" ? (
                          <IconActionButton
                            icon={<FiEyeOff aria-hidden="true" />}
                            label="Indisponível"
                            showLabel
                            className="secondary"
                            onClick={() => executeAction(photo, "unavailable")}
                            disabled={selectionDisabled}
                          />
                        ) : null}
                        {isAdmin && photo.availabilityStatus !== "PUBLISHED" && photo.publicationInputStatus !== "EXCLUDED" ? (
                          <IconActionButton
                            icon={<FiTrash2 aria-hidden="true" />}
                            label="Excluir"
                            showLabel
                            className="danger"
                            onClick={() => executeAction(photo, "exclude")}
                            disabled={selectionDisabled}
                          />
                        ) : null}
                      </div>
                    </div>
                  </div>
                </article>
              ))}
            </div>
            {pageSize !== "all" && pagination && pagination.totalPages > 1 ? (
              <PublicationPagination pagination={pagination} disabled={selectionDisabled}
                onPageChange={loadPublication} atEnd />
            ) : null}
          </section>
        ) : null}
      </main>

      {sourceUrl ? (
        <div
          className="publication-source-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="Fonte da foto"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) closeSource();
          }}
        >
          <div className="publication-source-dialog">
            <div className="publication-source-heading">
              <div>
                <span className="publication-panel-kicker">Inspeção protegida</span>
                <h2>Fonte da foto</h2>
                <p>Imagem usada na triagem administrativa. Ela não é entregue ao cliente.</p>
              </div>
              <button
                ref={sourceCloseButtonRef}
                type="button"
                className="publication-source-close"
                onClick={closeSource}
                aria-label="Fechar fonte da foto"
              >
                <FiX aria-hidden="true" />
                <span>Fechar</span>
              </button>
            </div>
            <img src={sourceUrl} alt="Fonte protegida da foto" />
          </div>
        </div>
      ) : null}
    </div>
  );
}

function Count({ label: countLabel, value }) {
  return (
    <div className="publication-count-card">
      <dt>{countLabel}</dt>
      <dd>{formatCount(value)}</dd>
    </div>
  );
}

function PublicationOverviewPanel({ distribution, counts }) {
  const total = Number(distribution?.total);
  const hasDistribution = Number.isFinite(total);

  const segments = (hasDistribution ? PUBLICATION_DISTRIBUTION_SEGMENTS : [])
    .map((segment, index) => ({
      ...segment,
      index,
      value: Number(distribution?.[segment.key] || 0),
    }))
    .filter((segment) => segment.value > 0);

  return (
    <section className="publication-distribution-card" aria-labelledby="publication-distribution-title">
      <div className="publication-distribution-heading">
        <div>
          <span className="publication-distribution-eyebrow">Leitura do acervo</span>
          <h2 id="publication-distribution-title">Panorama das fotos</h2>
          <p>{hasDistribution
            ? "Distribuição do acervo por estado atual. Cada foto aparece uma vez."
            : "Distribuição do acervo indisponível no momento."}</p>
        </div>
        {hasDistribution ? (
          <div className="publication-distribution-total">
            <strong>{formatCount(total)}</strong>
            <span>fotos no evento</span>
          </div>
        ) : null}
      </div>

      {hasDistribution ? <div
        className="publication-distribution-bar"
        role="img"
        aria-label={total > 0
          ? `Distribuição de ${formatCount(total)} fotos por estado operacional`
          : "Nenhuma foto disponível para distribuição"}
      >
        {segments.map((segment) => {
          const share = total > 0 ? (segment.value / total) * 100 : 0;
          return (
            <span
              key={segment.key}
              className={`publication-distribution-segment ${share >= 8 ? "has-label" : ""}`.trim()}
              style={{
                "--segment-color": segment.color,
                "--segment-order": segment.index,
                "--segment-share": `${share}%`,
              }}
              title={`${segment.label}: ${formatCount(segment.value)} (${Math.round(share)}%)`}
              aria-hidden="true"
            >
              {share >= 8 ? <span>{segment.label}</span> : null}
            </span>
          );
        })}
      </div> : null}

      {hasDistribution && segments.length ? (
        <ul className="publication-distribution-legend" aria-label="Legenda da distribuição das fotos">
          {segments.map((segment) => {
            const share = total > 0 ? (segment.value / total) * 100 : 0;
            return (
              <li key={segment.key}>
                <span
                  className="publication-distribution-swatch"
                  style={{ backgroundColor: segment.color }}
                  aria-hidden="true"
                />
                <span className="publication-distribution-label">{segment.label}</span>
                <strong>{formatCount(segment.value)}</strong>
                <small>{Math.round(share)}%</small>
              </li>
            );
          })}
        </ul>
      ) : hasDistribution ? (
        <p className="publication-distribution-empty">Nenhuma foto foi registrada neste evento.</p>
      ) : null}

      <section className="publication-count-section" aria-labelledby="publication-count-title">
        <div className="publication-count-heading">
          <h3 id="publication-count-title">Indicadores da operação</h3>
          <p>Estas contagens podem se sobrepor; não representam partes do total.</p>
        </div>
        <div className="publication-count-grid">
          <div className="publication-count-group">
            <h4>Em andamento</h4>
            <dl><Count label="Pendente" value={counts.processing} /></dl>
          </div>
          <div className="publication-count-group">
            <h4>Reconhecimento</h4>
            <dl>
              <Count label="Match" value={counts.matched} />
              <Count label="Sem Match" value={counts.noMatch} />
              <Count label="Foto geral" value={counts.generalCandidate} />
            </dl>
          </div>
          <div className="publication-count-group">
            <h4>Publicação</h4>
            <dl>
              <Count label="Disponível para publicação" value={counts.readyToPublish} />
              <Count label="Publicado" value={counts.published} />
              <Count label="Indisponível" value={counts.unavailable} />
            </dl>
          </div>
        </div>
      </section>
    </section>
  );
}

export default PublicationOperations;
